import { buildInitialPackagingMinimisationAssessmentSections } from '@artem-lex/compliance-documents'
import type {
  PackagingMinimisationAssessmentStoredEntry,
  PackagingMinimisationLegislationCriterion,
  PackagingMinimisationOutcomeResult,
  PackagingMinimisationStandardCriterion,
} from '@/shared/hooks/api/packagingSuppliers'
import { pickPackagingAssessmentProse } from './pickPackagingAssessmentProse'
import {
  mergePackagingMinimisationAssessmentHistoryIntoSections,
  type PackagingMinimisationCriteriaAgentResult,
  type PackagingMinimisationOutcomeAgentShape,
} from './packagingMinimisationAssessmentDocumentMergeHelpers'

function normalizeStandardCriterion(
  criterion: PackagingMinimisationStandardCriterion,
  useChinese: boolean
): PackagingMinimisationCriteriaAgentResult['productProtection'] {
  if ('explanationEn' in criterion) {
    return {
      isCriticalArea: criterion.isCriticalArea,
      explanation: pickPackagingAssessmentProse({
        en: criterion.explanationEn,
        zh: criterion.explanationZh,
        useChinese,
      }),
      followUpQuestion: pickPackagingAssessmentProse({
        en: criterion.followUpQuestionEn ?? undefined,
        zh: criterion.followUpQuestionZh ?? undefined,
        useChinese,
      }),
      mostImportantRequirement: pickPackagingAssessmentProse({
        en: criterion.mostImportantRequirementEn ?? undefined,
        zh: criterion.mostImportantRequirementZh ?? undefined,
        useChinese,
      }),
    }
  }

  return {
    isCriticalArea: criterion.isCriticalArea,
    explanation: criterion.explanation,
    followUpQuestion: criterion.followUpQuestion,
    mostImportantRequirement: criterion.mostImportantRequirement,
  }
}

function normalizeLegislationCriterion(
  criterion: PackagingMinimisationLegislationCriterion,
  useChinese: boolean
): PackagingMinimisationCriteriaAgentResult['legislationPC'] {
  if ('explanationEn' in criterion) {
    return {
      explanation: pickPackagingAssessmentProse({
        en: criterion.explanationEn,
        zh: criterion.explanationZh,
        useChinese,
      }),
    }
  }

  return { explanation: criterion.explanation }
}

function normalizeCriteriaAgentResult(
  entry: PackagingMinimisationAssessmentStoredEntry,
  useChinese: boolean
): PackagingMinimisationCriteriaAgentResult {
  const { criteria } = entry
  return {
    productProtection: normalizeStandardCriterion(
      criteria.productProtection,
      useChinese
    ),
    packagingManufacturingProcess: normalizeStandardCriterion(
      criteria.packagingManufacturingProcess,
      useChinese
    ),
    packingFillingProcess: normalizeStandardCriterion(
      criteria.packingFillingProcess,
      useChinese
    ),
    logistics: normalizeStandardCriterion(criteria.logistics, useChinese),
    productPresentationAndMarketing: normalizeStandardCriterion(
      criteria.productPresentationAndMarketing,
      useChinese
    ),
    userConsumerAcceptance: normalizeStandardCriterion(
      criteria.userConsumerAcceptance,
      useChinese
    ),
    informationPC: normalizeStandardCriterion(
      criteria.informationPC,
      useChinese
    ),
    safetyPC: normalizeStandardCriterion(criteria.safetyPC, useChinese),
    legislationPC: normalizeLegislationCriterion(
      criteria.legislationPC,
      useChinese
    ),
  }
}

function normalizeOutcome(
  outcome: PackagingMinimisationOutcomeResult,
  useChinese: boolean
): PackagingMinimisationOutcomeAgentShape {
  if ('reasoningEn' in outcome) {
    return {
      totalMinimumNecessaryWeightGrams:
        outcome.totalMinimumNecessaryWeightGrams,
      totalMinimumNecessaryVolumeCm3: outcome.totalMinimumNecessaryVolumeCm3,
      reasoning: pickPackagingAssessmentProse({
        en: outcome.reasoningEn,
        zh: outcome.reasoningZh,
        useChinese,
      }),
    }
  }

  return {
    totalMinimumNecessaryWeightGrams: outcome.totalMinimumNecessaryWeightGrams,
    totalMinimumNecessaryVolumeCm3: outcome.totalMinimumNecessaryVolumeCm3,
    reasoning: outcome.reasoning,
  }
}

export function buildPackagingMinimisationAssessmentDocumentSections(params: {
  locale: string
  historyEntry: PackagingMinimisationAssessmentStoredEntry
}) {
  const useChinese = params.locale.startsWith('zh')
  const agentResult = normalizeCriteriaAgentResult(
    params.historyEntry,
    useChinese
  )
  const outcome = normalizeOutcome(params.historyEntry.outcome, useChinese)
  const initialSections = buildInitialPackagingMinimisationAssessmentSections()
  return mergePackagingMinimisationAssessmentHistoryIntoSections(
    initialSections,
    agentResult,
    outcome
  )
}
